import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { apiFetch } from "../services/api";
import { useDeviceDetection } from "../hooks/useDeviceDetection";
import { createDevConsole } from "../utils/devLogger";

const console = createDevConsole();

// Textarea with entity autocomplete functionality
// Props:
// - value: string (controlled input value)
// - onChange: (newValue: string) => void
// - onSelect: (entity) => void (entity object {id,name,type,description,...})
// - placeholder?: string
// - minChars?: number (default 1)
// - maxResults?: number (default 10)
// - className?: string
// - rows?: number (default 6)
// - disabled?: boolean
const TextareaWithAutocomplete = ({
  value,
  onChange,
  onSelect,
  placeholder = "Type your description...",
  minChars = 1,
  maxResults = 10,
  className = "",
  rows = 6,
  disabled = false,
  /** Light surface (e.g. v2 embedded Add knowledge) */
  lightTheme = false,
}) => {
  const [allEntities, setAllEntities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [renderAbove, setRenderAbove] = useState(false);
  const textareaRef = useRef(null);
  const suggestionsRef = useRef(null);
  const loadedRef = useRef(false);
  const { isMobile } = useDeviceDetection();

  /** Embedded / light UI: grow with content so the page scrolls once (no textarea-in-scrollport). */
  const autoGrow = lightTheme;
  const minHeightRem = useMemo(() => Math.max(5.5, rows * 1.35 + 0.75), [rows]);

  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    if (!autoGrow) {
      el.style.height = "";
      el.style.overflowY = "";
      return;
    }
    el.style.overflowY = "hidden";
    el.style.height = "auto";
    const minPx = minHeightRem * 16;
    el.style.height = `${Math.max(minPx, el.scrollHeight)}px`;
  }, [value, autoGrow, minHeightRem]);

  // Load entities once (cached) for fast client-side filtering
  const ensureLoaded = useCallback(async () => {
    if (loadedRef.current || loading) return;
    console.log(
      "🔍 TextareaWithAutocomplete: Loading entities for autocomplete..."
    );
    setLoading(true);
    try {
      // Check if user is authenticated
      const token = localStorage.getItem("access_token");
      if (!token) {
        console.warn(
          "⚠️ TextareaWithAutocomplete: No authentication token found"
        );
        throw new Error("No authentication token");
      }

      const resp = await apiFetch("/api/entities?page_size=1000");
      console.log(
        "🔍 TextareaWithAutocomplete: API response status:",
        resp.status
      );

      if (!resp.ok) {
        console.error(
          "❌ TextareaWithAutocomplete: API request failed:",
          resp.status,
          resp.statusText
        );

        // Try fallback endpoint
        console.log("🔄 TextareaWithAutocomplete: Trying fallback endpoint...");
        const fallbackResp = await apiFetch("/api/entities-all");
        if (fallbackResp.ok) {
          const fallbackData = await fallbackResp.json();
          const fallbackEntities = fallbackData.entities || fallbackData || [];
          console.log(
            "✅ TextareaWithAutocomplete: Fallback successful, loaded",
            fallbackEntities.length,
            "entities"
          );
          setAllEntities(
            Array.isArray(fallbackEntities) ? fallbackEntities : []
          );
          loadedRef.current = true;
          return;
        }

        throw new Error(
          `API request failed: ${resp.status} ${resp.statusText}`
        );
      }

      const data = await resp.json();
      console.log("🔍 TextareaWithAutocomplete: API response data:", data);

      const entities = data.entities || data || [];
      console.log(
        "🔍 TextareaWithAutocomplete: Extracted entities:",
        entities.length
      );

      setAllEntities(Array.isArray(entities) ? entities : []);
      loadedRef.current = true;
      console.log("✅ TextareaWithAutocomplete: Entities loaded successfully");
    } catch (e) {
      console.error("❌ TextareaWithAutocomplete: Error loading entities:", e);
      console.error("❌ TextareaWithAutocomplete: Error details:", e.message);
      // Set empty array instead of swallowing error
      setAllEntities([]);
      loadedRef.current = true;
    } finally {
      setLoading(false);
    }
  }, [loading]);

  // Get word at cursor position
  const getWordAtCursor = useCallback((text, cursorPos) => {
    const beforeCursor = text.substring(0, cursorPos);
    const afterCursor = text.substring(cursorPos);

    // Find the start of the current word
    const wordStartMatch = beforeCursor.match(/([A-Za-z0-9_]+)$/);
    const wordStart = wordStartMatch
      ? beforeCursor.lastIndexOf(wordStartMatch[1])
      : -1;

    if (wordStart === -1) return { word: "", start: cursorPos, end: cursorPos };

    // Find the end of the current word
    const wordEndMatch = afterCursor.match(/^([A-Za-z0-9_]+)/);
    const wordEnd = wordEndMatch
      ? cursorPos + wordEndMatch[1].length
      : cursorPos;

    const word = text.substring(wordStart, wordEnd);
    return { word, start: wordStart, end: wordEnd };
  }, []);

  // Filter and sort suggestions
  const filteredSuggestions = useMemo(() => {
    const { word } = getWordAtCursor(value || "", cursorPosition);
    console.log(
      "🔍 TextareaWithAutocomplete: Filtering suggestions for word:",
      word,
      "minChars:",
      minChars
    );

    if (!word || word.length < minChars) {
      console.log(
        "🔍 TextareaWithAutocomplete: Word too short, no suggestions"
      );
      return [];
    }

    const term = word.toLowerCase();
    const isInterfaceType = (t) =>
      (t || "").toLowerCase().includes("interface");

    const filtered = allEntities
      .filter((e) => {
        const nameMatch = (e.name || "").toLowerCase().includes(term);
        const typeMatch = (e.type || "").toLowerCase().includes(term);
        return nameMatch || typeMatch;
      })
      .sort((a, b) => {
        const aIsInt = isInterfaceType(a.type);
        const bIsInt = isInterfaceType(b.type);
        if (aIsInt !== bIsInt) return aIsInt ? 1 : -1; // interfaces last
        // Secondary: prioritize name match strength (startsWith over contains)
        const aName = (a.name || "").toLowerCase();
        const bName = (b.name || "").toLowerCase();
        const aStarts = aName.startsWith(term) ? 0 : 1;
        const bStarts = bName.startsWith(term) ? 0 : 1;
        if (aStarts !== bStarts) return aStarts - bStarts;
        return aName.localeCompare(bName);
      })
      .slice(0, maxResults);

    console.log(
      "🔍 TextareaWithAutocomplete: Found",
      filtered.length,
      "suggestions for term:",
      term
    );
    console.log(
      "🔍 TextareaWithAutocomplete: Suggestions:",
      filtered.map((s) => s.name)
    );

    return filtered;
  }, [
    value,
    cursorPosition,
    allEntities,
    minChars,
    maxResults,
    getWordAtCursor,
  ]);

  // Update suggestions when filteredSuggestions change
  useEffect(() => {
    setSuggestions(filteredSuggestions);
    setShowSuggestions(filteredSuggestions.length > 0);
    setHighlightIndex(-1);
    
    // Check if we should render above or below
    if (filteredSuggestions.length > 0 && textareaRef.current) {
      const textareaRect = textareaRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - textareaRect.bottom;
      const spaceAbove = textareaRect.top;
      // On mobile, estimate height for 3 items; on desktop, use full list
      const visibleItems = isMobile ? Math.min(3, filteredSuggestions.length) : filteredSuggestions.length;
      const estimatedDropdownHeight = Math.min(isMobile ? 192 : 256, visibleItems * 50 + 20); // max-h-48 = 192px on mobile, max-h-64 = 256px on desktop
      
      // Render above if not enough space below but enough space above
      setRenderAbove(spaceBelow < estimatedDropdownHeight && spaceAbove > estimatedDropdownHeight);
    }
  }, [filteredSuggestions, isMobile]);

  // Handle textarea changes
  const handleTextareaChange = (e) => {
    const newValue = e.target.value;
    const newCursorPos = e.target.selectionStart;

    onChange?.(newValue);
    setCursorPosition(newCursorPos);

    // Load entities if not loaded
    ensureLoaded();
  };

  // Handle textarea key events
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    // On mobile, limit navigation to first 3 items
    const maxVisibleItems = isMobile ? Math.min(3, suggestions.length) : suggestions.length;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((prev) => {
        const next = prev + 1;
        return next >= maxVisibleItems ? 0 : next;
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((prev) => {
        const next = prev - 1;
        return next < 0 ? maxVisibleItems - 1 : next;
      });
    } else if (e.key === "Enter" && highlightIndex >= 0 && highlightIndex < maxVisibleItems) {
      e.preventDefault();
      selectSuggestion(suggestions[highlightIndex]);
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
      setHighlightIndex(-1);
    }
  };

  // Select a suggestion
  const selectSuggestion = (entity) => {
    const { start, end } = getWordAtCursor(value || "", cursorPosition);
    const beforeWord = (value || "").substring(0, start);
    const afterWord = (value || "").substring(end);
    const newValue = beforeWord + entity.name + afterWord;

    onChange?.(newValue);
    setShowSuggestions(false);
    setHighlightIndex(-1);

    // Focus back to textarea and set cursor position
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        const newCursorPos = start + entity.name.length;
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
        setCursorPosition(newCursorPos);
      }
    }, 0);

    onSelect?.(entity);
  };

  // Handle textarea click/focus
  const handleTextareaFocus = () => {
    ensureLoaded();
    const cursorPos = textareaRef.current?.selectionStart || 0;
    setCursorPosition(cursorPos);
  };

  // Handle textarea click
  const handleTextareaClick = () => {
    const cursorPos = textareaRef.current?.selectionStart || 0;
    setCursorPosition(cursorPos);
  };

  // Close suggestions on outside click
  useEffect(() => {
    const handler = (e) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(e.target) &&
        textareaRef.current &&
        !textareaRef.current.contains(e.target)
      ) {
        setShowSuggestions(false);
        setHighlightIndex(-1);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="relative">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleTextareaChange}
        onKeyDown={handleKeyDown}
        onFocus={handleTextareaFocus}
        onClick={handleTextareaClick}
        placeholder={placeholder}
        className={
          lightTheme
            ? `w-full rounded-xl border border-slate-300 bg-white px-3 py-3 text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/25 resize-none overflow-x-hidden ${className}`
            : `w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none ${className}`
        }
        rows={autoGrow ? 1 : rows}
        disabled={disabled}
      />

      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className={`autocomplete-dropdown absolute z-50 left-0 right-0 overflow-y-auto rounded-xl shadow-xl ${
            isMobile ? "max-h-48" : "max-h-64"
          } ${renderAbove ? "bottom-full mb-1" : "top-full mt-1"} ${
            lightTheme
              ? "border border-slate-200 bg-white ring-1 ring-slate-200/80"
              : "border-2 border-blue-500 bg-gray-800"
          }`}
          style={
            lightTheme
              ? { boxShadow: "0 12px 40px rgba(15, 23, 42, 0.12)" }
              : {
                  boxShadow:
                    "0 10px 25px rgba(0, 0, 0, 0.5), 0 0 0 2px rgba(59, 130, 246, 0.3)",
                }
          }
        >
          <div className="py-1">
            <div
              className={`px-3 py-1.5 border-b text-xs font-medium flex items-center gap-2 ${
                lightTheme
                  ? "border-slate-200 bg-slate-50 text-slate-700"
                  : "bg-blue-900/30 border-blue-500/50 text-blue-300"
              }`}
            >
              <span>💡</span>
              <span>Autocomplete suggestions ({suggestions.length})</span>
            </div>
            {(isMobile ? suggestions.slice(0, 3) : suggestions).map((entity, idx) => (
              <div
                key={entity.id}
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectSuggestion(entity);
                }}
                onMouseEnter={() => setHighlightIndex(idx)}
                className={`px-3 py-2 cursor-pointer text-sm flex items-center justify-between transition-colors ${
                  lightTheme
                    ? highlightIndex === idx
                      ? "bg-blue-600 text-white"
                      : "text-slate-800 hover:bg-slate-100"
                    : highlightIndex === idx
                      ? "bg-blue-600 text-white"
                      : "text-gray-200 hover:bg-gray-700"
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{entity.name}</div>
                  <div
                    className={`text-xs truncate ${
                      lightTheme
                        ? highlightIndex === idx
                          ? "text-blue-100"
                          : "text-slate-500"
                        : "text-gray-400"
                    }`}
                  >
                    {entity.type}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Loading indicator */}
      {loading && (
        <div
          className={`absolute top-2 right-2 text-xs ${
            lightTheme ? "text-slate-400" : "text-gray-400"
          }`}
        >
          Loading entities...
        </div>
      )}
    </div>
  );
};

export default TextareaWithAutocomplete;
